import React from 'react';
import { HelpCircle } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const FAQ = () => {
  const faqs = [
    {
      category: "Getting Started",
      questions: [
        {
          q: "What is MatrixLaunch?",
          a: "MatrixLaunch is a decentralized launchpad platform that connects crypto investors with carefully vetted early-stage blockchain projects. We provide a secure environment for token launches with built-in investor protection and a multi-level referral system."
        },
        {
          q: "How do I create an account?",
          a: "Click 'Sign Up' in the top right corner. You can register with email/password or connect your Web3 wallet (MetaMask, Trust Wallet, WalletConnect). After registration, complete KYC verification to unlock full platform features."
        },
        {
          q: "What wallets are supported?",
          a: "We support MetaMask, Trust Wallet, Coinbase Wallet, WalletConnect, and any Web3-compatible wallet. For mobile users, we recommend Trust Wallet or using WalletConnect with your preferred wallet app."
        },
        {
          q: "Is KYC mandatory?",
          a: "KYC is required for investments over $1,000. Basic verification (Level 1) requires email and phone. Enhanced verification (Level 2) needs government ID and proof of address. This ensures compliance with global regulations and protects the community from fraud."
        }
      ]
    },
    {
      category: "Investing",
      questions: [
        {
          q: "How do I invest in a project?",
          a: "Browse the Projects page, select a live project, review its details (team, tokenomics, audit report), click 'Invest Now', enter your amount, and confirm the transaction in your wallet. You'll receive confirmation via email once the blockchain transaction is complete."
        },
        {
          q: "What are the minimum and maximum investment amounts?",
          a: "Minimums vary by project (typically $50-$500). Maximums depend on your KYC level: Level 1 = $1,000, Level 2 = $50,000, Level 3 = Unlimited. Some projects implement anti-whale caps to ensure fair distribution."
        },
        {
          q: "When will I receive my tokens?",
          a: "Token distribution depends on the project's vesting schedule. Some projects distribute immediately, others have cliff periods (3-6 months) followed by linear vesting. Check each project's details page for specific vesting terms."
        },
        {
          q: "What fees does MatrixLaunch charge?",
          a: "We charge a 2% platform fee on successful investments. There are no deposit, withdrawal, or hidden fees. You also pay standard blockchain gas fees depending on the network used (Ethereum, BSC, Polygon)."
        },
        {
          q: "Can I get a refund if a project fails?",
          a: "Refunds are only available if a project doesn't reach its soft cap (minimum funding goal) or if there's a material breach of terms by the project team. Crypto investments are high-risk; only invest what you can afford to lose."
        },
        {
          q: "How do I track my investments?",
          a: "Visit your Dashboard to see all active investments, current values, ROI percentages, vesting schedules, and claimed tokens. We provide real-time updates synced with blockchain data."
        }
      ]
    },
    {
      category: "Referral Program",
      questions: [
        {
          q: "How does the referral program work?",
          a: "Share your unique referral link with others. When they sign up and invest, you earn commissions. Our 5-level system means you also earn from your referrals' referrals, creating passive income as your network grows."
        },
        {
          q: "What are the commission rates?",
          a: "Level 1 (direct referrals): 10%, Level 2: 5%, Level 3: 3%, Level 4: 2%, Level 5: 1%. Commissions are paid from platform revenue, not from user investments, making this sustainable long-term."
        },
        {
          q: "When do I receive referral commissions?",
          a: "Commissions are credited to your account instantly when your network members invest. You can withdraw anytime with no minimum threshold (though you'll pay network gas fees)."
        },
        {
          q: "How do I find my referral link?",
          a: "Log in, go to Dashboard → Referral section. Copy your unique link or referral code. You can also generate custom links with tracking parameters to measure campaign performance."
        },
        {
          q: "Is there a limit to how much I can earn?",
          a: "No earning caps! The more you build your network, the more passive income you generate. Top earners make $10,000-$50,000+ monthly through network effects."
        }
      ]
    },
    {
      category: "Security & Safety",
      questions: [
        {
          q: "How are projects vetted?",
          a: "Every project undergoes: (1) Team background checks and KYC, (2) Smart contract audit by CertiK/Hacken, (3) Tokenomics sustainability analysis, (4) Business model viability review, (5) Legal compliance check. Only 5-10% of applications are approved."
        },
        {
          q: "What if a project turns out to be a scam?",
          a: "While we conduct thorough due diligence, crypto investments carry inherent risk. We maintain an insurance fund to compensate victims of platform security breaches (not project failures). Always do your own research and never invest more than you can lose."
        },
        {
          q: "How is my personal data protected?",
          a: "We use AES-256 encryption for data at rest, TLS 1.3 for data in transit, and store sensitive information on secure, isolated servers. We're GDPR compliant and partner with certified KYC providers (Onfido, Sumsub)."
        },
        {
          q: "What should I do if I lose access to my wallet?",
          a: "MatrixLaunch doesn't hold your private keys – you maintain full custody. If you lose wallet access, use your seed phrase to recover it. We cannot reset or recover wallets. Always backup your seed phrase offline."
        },
        {
          q: "How can I report suspicious activity?",
          a: "Contact security@matrixlaunch.com immediately with details. We have a 24/7 security team monitoring for fraud, phishing attempts, and suspicious transactions."
        }
      ]
    },
    {
      category: "Technical",
      questions: [
        {
          q: "Which blockchain networks do you support?",
          a: "Currently: Ethereum (ETH), Binance Smart Chain (BSC), Polygon (MATIC). Coming soon: Avalanche, Arbitrum, Optimism. Each project specifies its launch network."
        },
        {
          q: "Why are gas fees so high on Ethereum?",
          a: "Ethereum network congestion drives up gas costs. We recommend using BSC or Polygon for lower fees, or transacting during off-peak hours (weekends, late night UTC)."
        },
        {
          q: "What happens if my transaction fails?",
          a: "Failed transactions usually occur due to insufficient gas, slippage, or network congestion. Your funds remain in your wallet, but you'll still pay gas fees. Increase gas limit/price and try again."
        },
        {
          q: "Can I use the platform on mobile?",
          a: "Yes! Our website is fully responsive. Use mobile wallet apps (Trust Wallet, MetaMask Mobile) with built-in browsers or WalletConnect for seamless mobile experience. Native iOS/Android apps launching Q2 2026."
        },
        {
          q: "Do you have an API for developers?",
          a: "API is in development and will launch Q2 2026. It will provide programmatic access to project data, investment stats, and wallet integration capabilities."
        }
      ]
    },
    {
      category: "Account & Support",
      questions: [
        {
          q: "How do I update my KYC information?",
          a: "Go to Dashboard → Settings → KYC. Upload new documents if information changed (address, legal name). Updates typically take 1-3 business days to review."
        },
        {
          q: "Can I delete my account?",
          a: "Yes, contact support@matrixlaunch.com to request account deletion. Note: we must retain certain data for 7 years per regulatory requirements, but it will be anonymized and not used for any purpose."
        },
        {
          q: "How do I enable two-factor authentication (2FA)?",
          a: "Dashboard → Settings → Security → Enable 2FA. We support Google Authenticator, Authy, and SMS-based 2FA. We strongly recommend 2FA for all accounts."
        },
        {
          q: "What if I forgot my password?",
          a: "Click 'Forgot Password' on login page, enter your email, and follow reset instructions. If you only connected via wallet, simply reconnect your wallet to access your account."
        },
        {
          q: "How can I contact support?",
          a: "Email: support@matrixlaunch.com (24-48hr response). Live chat: Available 9am-6pm EST. Community: Join our Telegram/Discord for peer support. For urgent security issues: security@matrixlaunch.com"
        },
        {
          q: "Do you offer customer support in other languages?",
          a: "Currently English only, but we're expanding to Spanish, Chinese (Mandarin), Japanese, and Korean in Q2 2026."
        }
      ]
    }
  ];

  return (
    <div className="dark-container min-h-screen pt-28 pb-20">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <div className="w-20 h-20 mx-auto mb-6 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center">
            <HelpCircle className="text-brand-primary" size={36} />
          </div>
          <h1 className="display-large mb-4" style={{ color: 'var(--text-primary)' }}>Frequently Asked Questions</h1>
          <p className="body-medium" style={{ color: 'var(--text-muted)' }}>Find answers to common questions about MatrixLaunch</p>
        </div>

        {faqs.map((section, idx) => (
          <div key={idx} className="mb-12">
            <h2 className="heading-1 mb-6" style={{ color: 'var(--text-primary)' }}>{section.category}</h2>
            <Accordion type="single" collapsible className="space-y-4">
              {section.questions.map((item, qIdx) => (
                <AccordionItem 
                  key={qIdx}
                  value={`${idx}-${qIdx}`}
                  className="bg-white/5 border border-white/25 backdrop-blur-xl rounded-sm px-6"
                >
                  <AccordionTrigger 
                    className="hover:text-brand-primary text-left py-5"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent 
                    className="pb-5 leading-relaxed"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        ))}

        {/* Contact CTA */}
        <div className="mt-16 bg-white/5 border border-brand-primary backdrop-blur-xl rounded-sm p-8 text-center">
          <h3 className="heading-1 mb-3" style={{ color: 'var(--text-primary)' }}>Still have questions?</h3>
          <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>
            Our support team is here to help 24/7
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/contact" className="btn-primary">
              Contact Support
            </a>
            <a href="https://t.me/matrixlaunch" target="_blank" rel="noopener noreferrer" className="btn-secondary">
              Join Telegram Community
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
